import type { DocumentError } from 'apiConnector/document/document';
import classNames from 'classnames';
import { MultiLanguageDocumentError } from 'components/shared/Document/MultiLanguageDocumentError';
import { FormattedMessage } from 'components/shared/Intl';
import { DocumentFormat } from 'epic/common-form/Documentation';
import type { IntlKey } from 'locales';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { FC } from 'react';
import type { IWorkflowError } from 'utils/errors/workflowErrors';

interface DocumentItemProps {
  id?: string;
  e2e: string;
  language: string;
  documentFormat: DocumentFormat;
  withFormatIcon?: boolean;
  isEnabled: boolean;
  onDocumentDownload: (language: string, documentFormat: DocumentFormat) => void;
  error?: DocumentError;

  errorContext?: ErrorDisplayContext;
}

export interface ErrorDisplayContext {
  productSubtype?: string;
  shouldDisplayErrorDetails: boolean;
  shouldDisplayStandardErrorMessage?: boolean;
  shouldUseRejectionCode?: boolean;
}
export function mapToLegacyError(error: NextWorkflowError): IWorkflowError {
  return {
    RejectionType: error.rejectionType,
    RejectionCode: error.rejectionCode,
    RejectionOrigin: error.rejectionOrigin,
    RejectionMessage: error.rejectionMessage,
    RejectionMessageCode: error.rejectionMessageCode,
    OriginMessage: error.originMessage,
    OutOfRangeLowLimit: error.outOfRangeLowLimit,
    OutOfRangeHighLimit: error.outOfRangeHighLimit,
    OutOfRangeCondition: error.outOfRangeCondition,
  };
}

function getDocumentFormatIcon(documentFormat: DocumentFormat): string {
  switch (documentFormat) {
    case 'PDF':
      return 'picture_as_pdf';
    case 'Word':
    default:
      return 'library_books';
  }
}

export const MultiLanguageDocumentItem: FC<DocumentItemProps> = ({
  id = 'downloadTermsheet-error-icons',
  onDocumentDownload,
  language,
  documentFormat,
  isEnabled,
  e2e,
  error,
  errorContext,
  withFormatIcon = false,
}) => {
  const onDownload = () => onDocumentDownload(language, documentFormat);

  return (
    <div className="d-flex mx-1">
      {language !== '' ? (
        <button
          key={language}
          className={classNames('btn btn-xs btn-flat d-flex align-items-center link-secondary px-1', {
            disabled: !isEnabled,
            'text-link': isEnabled,
            'text-danger': error !== undefined,
          })}
          data-e2e={`${e2e}-download-button-${language}-${documentFormat}`}
          onClick={onDownload}
        >
          <FormattedMessage id={`languageCode.${language}` as IntlKey} defaultMessage={language} />
          {withFormatIcon && <i className="icon icon-sm ms-1 icon-outlined">{getDocumentFormatIcon(documentFormat)}</i>}
        </button>
      ) : (
        <button
          data-e2e={e2e}
          type="button"
          className={classNames(
            'btn btn-sm btn-flat-secondary d-flex flex-row align-items-center justify-content-center px-0',
          )}
          onClick={onDownload}
        >
          <i className="icon icon-sm">save_alt</i>
        </button>
      )}

      {!!error && (
        <MultiLanguageDocumentError
          id={id}
          e2e={e2e}
          lang={language}
          documentFormat={documentFormat}
          error={error}
          errorContext={errorContext}
          initOpen
        />
      )}
    </div>
  );
};
